import React, { useCallback, useMemo, useState } from 'react';
import { OneWayInServiceDto, OneWayInServiceProviderDto } from 'src/api/v2';
import useOneWayInServices from 'src/features/one-way-in/hooks/use-one-way-in-services';
import DataTableV3 from 'src/components/DataTableV3';
import { getServiceColumns } from '../../constants/service-columns';
import ServiceInfoPanel from './ServiceInfoPanel';
import './ServiceTable.scss';
import { useTranslation } from 'react-i18next';
import { DataTableV3Ids } from 'src/constants/dataTableIds';

type Props = {
  selectedActor?: OneWayInServiceProviderDto;
  selectedServices?: OneWayInServiceDto[];
  setSelectedServices?: React.Dispatch<React.SetStateAction<OneWayInServiceDto[]>>;
};

const ServiceTable: React.FC<Props> = ({ selectedActor, selectedServices, setSelectedServices }) => {
  const { t: tOneWayIn } = useTranslation('oneWayIn');
  const { activeClusterServices, isLoading } = useOneWayInServices(selectedActor ? selectedActor.id : '');

  const [infoService, setInfoService] = useState<OneWayInServiceDto | undefined>(undefined);

  const preselectedRows = useMemo(
    () => Object.fromEntries((selectedServices ?? []).map((service) => [service.id, true])),
    [selectedServices],
  );

  const toggleInfo = useCallback((service: OneWayInServiceDto) => {
    setInfoService((current) => (current?.id === service.id ? undefined : service));
  }, []);

  const columns = useMemo(
    () =>
      getServiceColumns({
        onToggleInfo: toggleInfo,
        infoAriaLabel: tOneWayIn('referring.servicesTable.showInfo'),
      }),
    [toggleInfo, tOneWayIn],
  );

  return (
    <div className="service-table-container">
      <div className="service-table">
        <div className="service-table--table">
          <DataTableV3
            id={DataTableV3Ids.OneWayIn.OpportunityReferringServiceInfo}
            disableGlobalFilter={true}
            hideFilterText
            data={activeClusterServices}
            columns={columns}
            pageSizeOptions={[5, 10, 25, 100]}
            pageSize={25}
            disableColumnEditor
            disablePagination
            disableExport
            getRowId={(row) => row.id}
            preselectedRows={preselectedRows}
            rowsSelectedSingularTranslation={tOneWayIn('referring.servicesTable.selectedRowsSingular')}
            rowsSelectedPluralTranslation={tOneWayIn('referring.servicesTable.selectedRowsPlural')}
            setSelectedRows={setSelectedServices}
            loading={isLoading}
          />
        </div>
        {infoService && (
          <>
            <div className="service-table--divider" />
            <div className="service-table--info">
              <ServiceInfoPanel service={infoService} />
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default ServiceTable;
